'use client'

import { styled } from 'styled-components'

import { Button, ButtonAsA, Tag } from '@b-stock/fe-core/bstock-react'
import {
  Breakpoints,
  Colors,
  Shadows,
  Typography,
} from '@b-stock/fe-core/design-system'

import { cardHeight, cardWidth } from './dimensions'

/**
 * Top padding aligns the card's top edge with the lead carousel title row.
 * Flex column so the card can fill whatever height the row layout gives the
 * slot (see `fillDashboardColumn` in HomeDashboardRowLayout).
 */
export const Outer = styled.div`
  width: 100%;
  padding: 3rem 0 0;
  display: flex;
  flex-direction: column;
`

/**
 * Mobile keeps a fixed height so the skeleton and loaded shell swap without
 * layout shift while stacked. On md+ the card fills the slot instead, so its
 * height tracks the carousel column beside it.
 */
export const Card = styled.section`
  ${Shadows.Bottom}
  border-radius: 1rem;
  padding: 1.25rem;
  background: ${Colors.Semantic.Neutral.white};
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  height: ${cardHeight};

  @media ${Breakpoints.min.medium} {
    width: ${cardWidth};
    max-width: ${cardWidth};
    height: auto;
    flex: 1;
    min-height: 0;
  }
`

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`

export const Title = styled.h2`
  ${Typography.Title2}
  margin: 0;
  min-width: 0;
`

export const LiveSlot = styled.span<{ $live: boolean }>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.3125rem;
  ${Typography.Body4_Semibold}
  color: ${({ $live }) =>
    $live ? Colors.Semantic.Success.copy : Colors.Semantic.Neutral.dark_grey};
`

export const LiveDot = styled.span<{ $live: boolean }>`
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 50%;
  background: ${({ $live }) =>
    $live
      ? Colors.Semantic.Success.onhover
      : Colors.Semantic.Neutral.medium_grey};
`

export const SegmentedTabList = styled.div`
  display: flex;
  border: 0.0625rem solid ${Colors.Semantic.Neutral.medium_grey};
  border-radius: 0.25rem;
  overflow: hidden;
  flex-shrink: 0;
`

export const SegmentedTab = styled.button<{ $active: boolean }>`
  ${Typography.Subtitle2}
  flex: 1;
  border: none;
  cursor: pointer;
  padding: 0.5rem 0;
  background: ${({ $active }) =>
    $active
      ? Colors.Semantic.BStock.default.lightBackground
      : Colors.Semantic.Neutral.white};
  color: ${({ $active }) =>
    $active
      ? Colors.Semantic.Neutral.white
      : Colors.Semantic.Neutral.very_dark_grey};
  transition:
    background 0.15s ease-in-out,
    color 0.15s ease-in-out;
`

export const PanelArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`

export const CtaStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: auto;
  flex-shrink: 0;
`

export const DashboardRowList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

/** 15%-alpha row border per tint, over the matching semantic background. */
const rowTintStyles = {
  error: {
    border: `${Colors.Semantic.Error.onhover}26`,
    background: Colors.Semantic.Error.background,
  },
  warning: {
    border: `${Colors.Semantic.Warning.onhover}26`,
    background: Colors.Semantic.Warning.background,
  },
} as const

export type DashboardRowTint = keyof typeof rowTintStyles

export const DashboardRowItem = styled.li<{ $tint?: DashboardRowTint }>`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid
    ${({ $tint }) =>
      $tint
        ? rowTintStyles[$tint].border
        : Colors.Semantic.Neutral.medium_grey};
  background: ${({ $tint }) =>
    $tint ? rowTintStyles[$tint].background : Colors.Semantic.Neutral.white};
`

export const DashboardRowTopLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`

export const DashboardRowTitle = styled.span`
  ${Typography.Subtitle2}
  color: ${Colors.Semantic.Neutral.very_dark_grey};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const DashboardRowTag = styled(Tag)`
  flex-shrink: 0;
`

const DashboardRowMetaLine = styled.div`
  ${Typography.Body4}
  color: ${Colors.Semantic.Neutral.dark_grey};
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem 0.625rem;
`

/** Bid meta stays on one line: the amounts truncate so the button fits. */
export const BidRowMetaLine = styled(DashboardRowMetaLine)`
  flex-wrap: nowrap;
`

export const BidRowAmounts = styled.span`
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const BidRowTimeRemaining = styled.span<{ $urgent: boolean }>`
  ${Typography.Body4_Semibold}
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: 0.25rem;
  white-space: nowrap;
  color: ${({ $urgent }) =>
    $urgent
      ? Colors.Semantic.Error.highlight
      : Colors.Semantic.Neutral.very_dark_grey};
`

/** Order meta stays on one line: the text truncates so the button fits. */
export const OrderRowMetaLine = styled(DashboardRowMetaLine)`
  flex-wrap: nowrap;
`

export const OrderRowMetaText = styled.span`
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

/** The row's single status-dependent action, pinned to the meta line's end. */
export const OrderActionButton = styled(ButtonAsA)`
  margin-left: auto;
  flex-shrink: 0;
`

export const DashboardRowEmphasis = styled.strong`
  ${Typography.Body4_Semibold}
  color: ${Colors.Semantic.Neutral.very_dark_grey};
`

export const PanelStateContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem;
  text-align: center;
`

export const PanelStateTitle = styled.p`
  ${Typography.Subtitle1}
  margin: 0;
  color: ${Colors.Semantic.Neutral.very_dark_grey};
`

export const PanelStateMessage = styled.p`
  ${Typography.Body3}
  margin: 0;
  color: ${Colors.Semantic.Neutral.dark_grey};
`

export const RaiseBidButton = styled(Button)`
  margin-left: auto;
  flex-shrink: 0;
`

/** Stepper group, Confirm, Cancel on one row; the error line wraps below. */
export const RebidStepperContainer = styled.div<{ $tint?: DashboardRowTint }>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.125rem;
  padding-top: 0.5rem;
  border-top: 1px solid
    ${({ $tint }) =>
      $tint
        ? rowTintStyles[$tint].border
        : Colors.Semantic.Neutral.medium_grey};
`

export const RebidInputRow = styled.div<{ $hasError: boolean }>`
  display: flex;
  align-items: stretch;
  height: 1.875rem;
  border: 1px solid
    ${({ $hasError }) =>
      $hasError
        ? Colors.Semantic.Error.highlight
        : Colors.Semantic.Neutral.medium_grey};
  border-radius: 0.25rem;
  overflow: hidden;
  background: ${Colors.Semantic.Neutral.white};

  &:focus-within {
    border-color: ${({ $hasError }) =>
      $hasError
        ? Colors.Semantic.Error.highlight
        : Colors.Semantic.Neutral.dark_grey};
  }
`

export const RebidStepButton = styled.button`
  ${Typography.Subtitle2}
  width: 1.75rem;
  flex-shrink: 0;
  border: none;
  background: ${Colors.Semantic.Neutral.white};
  color: ${Colors.Semantic.Neutral.very_dark_grey};
  cursor: pointer;

  &:first-child {
    border-right: 1px solid ${Colors.Semantic.Neutral.medium_grey};
  }

  &:last-child {
    border-left: 1px solid ${Colors.Semantic.Neutral.medium_grey};
  }

  &:disabled {
    color: ${Colors.Semantic.Neutral.medium_grey};
    cursor: not-allowed;
  }
`

export const RebidAmountBox = styled.div`
  width: 6rem;
  display: flex;
  align-items: center;
  padding: 0 0.5rem;
`

export const RebidCurrencyPrefix = styled.span`
  ${Typography.Body4}
  color: ${Colors.Semantic.Neutral.dark_grey};
  margin-right: 0.125rem;
  flex-shrink: 0;
`

export const RebidAmountInput = styled.input`
  flex: 1;
  min-width: 0;
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  ${Typography.Body4}
  color: ${Colors.Semantic.Neutral.very_dark_grey};
`

export const RebidErrorText = styled.div`
  ${Typography.Caption}
  flex-basis: 100%;
  color: ${Colors.Semantic.Error.highlight};
`

export const SkeletonBlock = styled.div`
  border-radius: 0.25rem;
  background: linear-gradient(270deg, transparent -25%, currentcolor 100%);
  animation: orders-bids-dashboard-skeleton-pulse 1s ease-in-out 0.5s infinite;
  opacity: 0.1;

  @keyframes orders-bids-dashboard-skeleton-pulse {
    0% {
      opacity: 0.1;
    }

    50% {
      opacity: 0.05;
    }

    100% {
      opacity: 0.1;
    }
  }
`

export const SkeletonTitleLine = styled(SkeletonBlock)`
  width: 70%;
  height: 1rem;
`

export const SkeletonMetaLine = styled(SkeletonBlock)`
  width: 90%;
  height: 0.875rem;
`
